import { ActivatedRoute } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';
import { SponsorRouteData } from '../sponsor-route-data';
import { BehaviorSubject, ConnectableObservable, Subscription, combineLatest, Observable } from 'rxjs';
import { LabTestResult } from '../../lab-test-results/lab-test-result';
import { map, publishReplay } from 'rxjs/operators';
import { SponsorLabResultsStorage } from '../lab-results/sponsor-lab-results-storage';
import { UnitOption } from '../../lab-test-results/unit-option';
import { LoDashExplicitArrayWrapper, includes, chain, isEmpty } from 'lodash';
import { SubjectLabTestsDataSource } from '../../subjects/subject-lab-tests-data-source';
import { UnitStorage } from '../../user/unit-storage';
import { routeParams, routeData } from '../../shared/route-helpers';
import { SponsorSubjectIdentity } from '../sponsor-subject-item';

@Injectable()
export class SponsorSelectedSubjectLabResultsStorage implements OnDestroy, SubjectLabTestsDataSource {
    public readonly showOnlyFlagged = new BehaviorSubject(false);

    private readonly labTestResults$: ConnectableObservable<LabTestResult[]>;
    private connection: Subscription;

    constructor(
        sponsorRouteData: SponsorRouteData,
        labResultsStorage: SponsorLabResultsStorage,
        unitStorage: UnitStorage,
        route: ActivatedRoute,
    ) {
        const selectedStudy = sponsorRouteData.selectedStudy;
        
        const subjectItems$: Observable<SponsorSubjectIdentity[]> = route.data.pipe(map(d => d[routeData.sponsorSubjectIdentities]));
        const selectedZcsubjectId$: Observable<string> = route.params.pipe(map(p => p[routeParams.zcSubjectId]));
        const selectedSubjectIdentity$ = combineLatest([subjectItems$, selectedZcsubjectId$])
            .pipe(
                map(([subjects, selectedZcsubjectId]) => {
                    if (isEmpty(subjects)) {
                        return null;
                    }
                    const subject = subjects.find(s => s.zcSubjectId === selectedZcsubjectId);       
                    return subject;
                }
            ));

        this.labTestResults$ = combineLatest([
            this.showOnlyFlagged,
            unitStorage.currentUnit,
            selectedSubjectIdentity$,
            labResultsStorage.loadSponsorSubjectLabResults(selectedStudy, selectedSubjectIdentity$.pipe(map(subject => subject && subject.subjectId.toString()))),
        ]).pipe(
            map((data: [boolean, UnitOption, SponsorSubjectIdentity, LabTestResult[]]) => {
                const [onlyFlagged, unitOption, selectedSubject, res] = data;
                let result: LabTestResult[] = null; // show spinner
                if (res) {
                    let resultChain = chain(res);

                    if (onlyFlagged) {
                        resultChain = this.filterFlaggedTests(resultChain);
                    }

                    result = resultChain.map(r => {
                        r.subjectSiteId = selectedSubject && selectedSubject.siteId;
                        r.showResultsInSI = unitOption.isSiUnit;
                        return r;
                    }).value();
                }

                return result;
            }),
            publishReplay(1)
        ) as ConnectableObservable<LabTestResult[]>;

        this.connection = this.labTestResults$.connect();
    }

    public get labTestResults(): Observable<LabTestResult[]> {
        return this.labTestResults$;
    }

    public ngOnDestroy() {
        this.connection.unsubscribe();
    }

    private filterFlaggedTests(resChain: LoDashExplicitArrayWrapper<LabTestResult>): LoDashExplicitArrayWrapper<LabTestResult> {
        const flaggedUniqLabTestsKeys = resChain
            .filter(labTestResult => labTestResult.isFlagged)
            .map(labTestResult => labTestResult.uniqLabTestIdKey)
            .uniq()
            .value();

        return resChain.filter(labTestResult => includes(flaggedUniqLabTestsKeys, labTestResult.uniqLabTestIdKey));
    }
}
